import scraperApi from "api";
import Toaster from "components/Toaster";
import { saveAs } from "file-saver";
import { format, parseISO } from "date-fns";
import { DateRange, Filters } from "./types";
import { buildAnalyticsApiParams } from "./filters";

type DownloadType = 'domains' | 'error-logs';

type DownloadArgs = {
  dateRange: DateRange;
  filters?: Filters;
  filenamePrefix: string;
  type?: DownloadType;
  extraParams?: Record<string, unknown>;
};

const buildFilename = (prefix: string, startDate?: string, endDate?: string) => {
  // Params are UTC ISO strings; name the file by the viewer's own calendar dates.
  const toLocalDay = (value?: string) => (value ? format(parseISO(value), "yyyy-MM-dd") : undefined);

  const start = toLocalDay(startDate);
  const end = toLocalDay(endDate);

  if (start && end) {
    return `${prefix}-${start}-to-${end}.zip`;
  }

  return `${prefix}-${format(new Date(), "yyyy-MM-dd")}.zip`;
};

export const downloadAnalyticsCsvFromApi = async ({
  dateRange,
  filters,
  filenamePrefix,
  type = 'domains',
  extraParams = {},
}: DownloadArgs) => {
  if (!dateRange[0] || !dateRange[1]) {
    Toaster.error("Please select a start and end date before downloading.");
    throw new Error("Missing date range for analytics download");
  }

  try {
    const params = buildAnalyticsApiParams(dateRange, filters, extraParams);

    // Call the appropriate endpoint based on type
    const blob = type === 'error-logs'
      ? await scraperApi.analytics.downloadErrorLogs(params)
      : await scraperApi.analytics.downloadDomainData(params);

    if (!blob) throw new Error("Empty analytics download response");

    saveAs(blob, buildFilename(filenamePrefix, params.startDate, params.endDate));
  } catch (error) {
    Toaster.error("Failed to download analytics report. Please try again.");
    throw error;
  }
};

